import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { readReviewDeepLink } from '../../../utils/reviewDeepLink';
import InspectionContext from '../../../contexts/InspectionContext';
import { ProjectContext } from '../../../contexts/ProjectContext';
import { withProjectContext } from '../../../hoc/withProjectContext';
import { useClient } from '../../../hooks/client';
import { getInspectionTypeLabel } from '../../../utils';
import NavTabs from '../../Shared/NavTabs';
import Loading from '../../Shared/ResourceIndex/Loading';
import InspectionCertification from '../Certification/InspectionCertification';
import InspectionQuestions from '../Details/InspectionQuestions';
import Findings from '../Findings/Findings';
import InspectionHeader from '../Header/InspectionHeader';
import InspectionMap from '../Map/InspectionMap';
import InspectionPrint from './InspectionPrint';
import SiteMapPrint from './SiteMapPrint';
import AppContext from '../../../contexts/AppContext';
import { AppDivisionContext } from '../../../contexts/AppDivisionContext';
import TenantFeatureContext from '../../../contexts/TenantContext';
import commentableApi from '../../../utils/api/commentable';
import NOAAWeatherTable from '../Certification/NOAAWeatherTable';
import schedulerApi from '../../../utils/api/inspectionSchedular';
import InspectionAssets from '../Asset/InspectionAssets';

function InspectionDetailUI() {
  const projectContext = useContext(ProjectContext)
  const inspectionContext = useContext(InspectionContext)
  const featureContext = useContext(TenantFeatureContext);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const appDivisionContext = useContext(AppDivisionContext);
  const match = useRouteMatch<{ id: string }>();
  const [modal, setModal] = useState<null | 'print' | 'siteMapPrint'>(null);
  const [showModal, setShowModal] = useState<string>('');
  const [initialNotes, setInitialNotes] = useState<any>(null);
  const [isAssetTabEnabled, setIsAssetTabEnabled] = useState<boolean>();
  const isPublic = match.path.startsWith('/public');
  const location = useLocation();

  // SW2-2248 (Oct 9): the review email's Approve / Reject links open `/inspection/:id?review=…`.
  // The Certification tab is where both happen, so take the link there; it acts on it.
  useEffect(() => {
    if (isPublic || !readReviewDeepLink(location.search)) return;

    if (!location.pathname.endsWith('/certification')) {
      history.replace(`${match.url.replace(/\/$/, '')}/certification${location.search}`);
    }
  }, [location.pathname, location.search]);

  const clientQuery = projectContext.project ? useClient(projectContext.project.client_id) : null;

  const userTenantId =
    appContext &&
    appContext.state &&
    appContext.state.user &&
    appContext.state.user.tenantId;
  const clientTenantId =
    projectContext &&
    projectContext.project &&
    projectContext.project.client &&
    projectContext.project.client.tenant_id;
  const projectId =
    projectContext && projectContext.project && projectContext.project.id;

  useEffect(() => {
    if (!userTenantId || !clientTenantId) {
      return;
    } else if (userTenantId !== clientTenantId) {
      history.push(appDivisionContext.getPath('/projects'));
    }
  }, [userTenantId, clientTenantId]);

  useEffect(() => {
    const fetchInspectionForms = async () => {
      if (projectId) {
        try {
          const forms = await schedulerApi.getProjectForms(projectId);
          const assetTabEnabled =
            forms.data?.find(
              (form: any) =>
                form.template_id === inspectionContext.inspection.template_id &&
                form.is_asset_tracking_enabled,
            );

          setIsAssetTabEnabled(!!assetTabEnabled?.is_asset_tracking_enabled);
        } catch {
          setIsAssetTabEnabled(false);
        }
      }
    };

    fetchInspectionForms();
  }, [projectId, inspectionContext.inspection.template_id]);

  useEffect(() => {
    if (projectContext.project?.external_project) {
      history.push(`/projects/${projectContext.project.id}/inspections`)
    }
    updateInitialNotes();
  }, []);

  const handleAddToReport = () => {
    const weatherDatas = JSON.parse(localStorage.getItem('weather' + projectContext.project?.id) || '[]');

    let weatherDataFormatted = 'NOAA Forecast: \n';

    if (weatherDatas) {
      for (let i = 0; i < weatherDatas.length; i++) {
        weatherDataFormatted += `${new Date(weatherDatas[i].time).getMonth() + 1}/${new Date(weatherDatas[i].time).getDate()}/${new Date(weatherDatas[i].time).getFullYear()}: ${weatherDatas[i].detailedForecast} \n`;
      }
    }

    return commentableApi
      .create('inspections', inspectionContext.inspection.id, {
        note: { content: weatherDataFormatted },
      })
      .then(() => {
        setShowModal('success');
        setTimeout(() => {
          setShowModal('');
          updateInitialNotes();
        }, 2000);
      });
  };

  const updateInitialNotes = () => {
    commentableApi.index("inspections", inspectionContext.inspection.id).then(notes => {
      setInitialNotes(notes);
    });
  }

  if (
    !projectContext.project ||
    !inspectionContext.inspection ||
    isEmpty(inspectionContext.inspection)
  ) {
    return (
      <section className='inspection-detail'>
        <Loading what='inspection details' showLoadingText />
      </section>
    );
  }

  return (
    <section className='inspection-detail'>
      <InspectionHeader
        title={getInspectionTypeLabel(inspectionContext.inspection)}
        clientName={clientQuery && clientQuery.data ? clientQuery.data.name : null}
        customTab={clientQuery && clientQuery.data ? clientQuery.data.custom_tab : null}
        projectName={projectContext.projectName}
        enablePrint
        handlePrint={() => setModal('print')}
        handleSiteMapPrint={() => setModal('siteMapPrint')}
        inspectionNumber={inspectionContext.inspection.number}
        inspectionId={inspectionContext.inspection.id}
        handleAddToReport={handleAddToReport}
        showModal={showModal}
      />

      <NavTabs
        tabs={[
          {
            to: '/questionnaire',
            label: 'Inspection Questions',
            Component: InspectionQuestions,
          },
          {
            label: 'Findings',
            to: '/findings',
            showTab: (func) => func('read', 'Findings') || isPublic,
            Component: Findings,
          },
          {
            label: 'Assets',
            to: '/assets',
            showTab: () => featureContext.tenantHasFeature('Asset Tracking') && isAssetTabEnabled,
            Component: InspectionAssets,
          },
          {
            label: 'Map',
            to: '/map',
            Component: InspectionMap,
          },
          {
            label: ' Weather Table',
            to: '/weather-table',
            showTab: () => projectContext.project?.forecast_weather && clientQuery && clientQuery.data?.forecast_weather && featureContext.tenantHasFeature('NOAA Weather Table') ? true : false,
            Component: NOAAWeatherTable
          },
          {
            label: 'Certification',
            to: '/certification',
            showTab: (func) =>
              func('read', 'Certify') || func('read', 'Sign Off'),
            Component: InspectionCertification,
            getProps: () => ({ initialNotes }),
          },
        ]}
        url={match.url}
        tabsReady={
          (isPublic || featureContext.featuresLoaded) &&
          isAssetTabEnabled !== undefined
        }
      />
      {/* load modals! */}
      {modal === 'print' ? (
        <InspectionPrint
          onClose={() => setModal(null)}
          assetTrackingEnabled={
            featureContext.tenantHasFeature('Asset Tracking') &&
            isAssetTabEnabled
          }
        />
      ) : null}
      {modal === 'siteMapPrint' ? (
        <SiteMapPrint onClose={() => setModal(null)} projectLevelEsriEnabled={projectContext.project?.esri_layers_enabled} isGISEnabled={featureContext.tenantHasFeature('GIS Configuration')} />
      ) : null}
    </section>
  );
}

function InspectionDetail() {
  return (
    <InspectionDetailUI />
  )
}

export default withProjectContext(InspectionDetail);
